<script setup lang="ts">
import {
  onMounted,
  onUnmounted,
  ref,
  type FunctionalComponent,
  type HTMLAttributes,
  type VNodeProps
} from "vue"
import {
  ExclamationIcon,
  XCircleIcon,
  CheckCircleIcon
} from "@heroicons/vue/solid"
import { XIcon } from "@heroicons/vue/outline"
import { useSystemFeedback } from "@/stores/systemFeedback"

interface Props {
  type?: "danger" | "success" | "warning" | "dark" | null
  title?: string | null
  timeout?: number // in seconds
  text: string
  customIcon?: FunctionalComponent<HTMLAttributes & VNodeProps> | null
  hasCloseIcon?: boolean
}

enum alertTextColor {
  danger = "text-[#FF5E5E]",
  success = "text-[#1FC570]",
  warning = "text-[#FF9A5C]",
  dark = "text-[#FFFFFF]"
}

enum alertGeneralColors {
  danger = "bg-[#FF5E5E] border-[#FF5E5E]",
  success = "bg-[#1FC570] border-[#1FC570]",
  warning = "bg-[#FF9A5C] border-[#FF9A5C]",
  dark = "bg-[#000000] border-[#FFFFFF]"
}

const props = withDefaults(defineProps<Props>(), {
  title: null,
  type: null,
  timeout: 0,
  customIcon: null,
  hasCloseIcon: false
})
const emit = defineEmits(["close-alert"])
const systemFeedbackStore = useSystemFeedback()
const generalColorClasses = ref<string>()
const textColorsClass = ref<string>()
const timeoutBarSize = ref(props.timeout > 0 ? 100 : 0)
const timeoutProgress =
  props.timeout > 0
    ? setInterval(() => {
        timeoutBarSize.value = timeoutBarSize.value - 5 / props.timeout
      }, 50)
    : 0

function handleAlertClose() {
  systemFeedbackStore.resetFeedback()
  emit("close-alert")
}

onMounted(() => {
  if (props.timeout > 0) {
    setTimeout(() => clearInterval(timeoutProgress), props.timeout * 1000)
  }

  generalColorClasses.value =
    alertGeneralColors[props.type as string as keyof typeof alertGeneralColors]

  textColorsClass.value =
    alertTextColor[props.type as string as keyof typeof alertTextColor]
})

onUnmounted(() => useSystemFeedback().resetFeedback())
</script>

<template>
  <div v-if="timeout === 0 || timeoutBarSize > 0">
    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-0"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-0"
    >
      <div
        class="flex flex-col text-sm rounded-md overflow-hidden border border-opacity-25 bg-opacity-10 backdrop-blur-md"
        :class="[generalColorClasses, textColorsClass]"
        role="alert"
      >
        <div v-if="title" class="flex justify-between gap-2 p-4">
          <div class="flex gap-2">
            <div v-if="customIcon || (type !== 'dark' && type !== null)">
              <component :is="customIcon" v-if="customIcon" class="h-5 w-5" />
              <XCircleIcon v-else-if="type === 'danger'" class="h-5 w-5" />
              <ExclamationIcon v-else-if="type === 'warning'" class="h-5 w-5" />
              <CheckCircleIcon v-else-if="type === 'success'" class="h-5 w-5" />
            </div>
            <div class="flex flex-col gap-2">
              <div v-if="title" class="font-medium">{{ title }}</div>
            </div>
          </div>
          <div class="flex gap-2">
            <slot name="content-right" />
            <button
              v-if="hasCloseIcon"
              class="hover:opacity-50 transition-all"
              @click="handleAlertClose"
            >
              <XIcon :class="[textColorsClass, 'h-6 w-6 text-white']" />
            </button>
          </div>
        </div>
        <div v-if="text" :class="[title ? 'pb-4' : 'py-4', 'px-4']">
          <span v-html="text"></span>
        </div>
        <div
          v-if="timeout > 0"
          class="py-1 bg-black bg-opacity-40 transition-all"
          :style="{ width: timeoutBarSize + '%' }"
        ></div>
      </div>
    </transition>
  </div>
</template>
